import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import "@/App.css";
import { AuthProvider } from "@/lib/auth";
import { CartProvider } from "@/lib/cart";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import Home from "@/pages/Home";
import World from "@/pages/World";
import Chapters from "@/pages/Chapters";
import ChapterDetail from "@/pages/ChapterDetail";
import Collections from "@/pages/Collections";
import CollectionDetail from "@/pages/CollectionDetail";
import ProductDetail from "@/pages/ProductDetail";
import { Campaigns, CampaignDetail } from "@/pages/Campaigns";
import Gifts from "@/pages/Gifts";
import Founder from "@/pages/Founder";
import Stores from "@/pages/Stores";
import About from "@/pages/About";
import Careers from "@/pages/Careers";
import FAQ from "@/pages/FAQ";
import Contact from "@/pages/Contact";
import { Journal, JournalDetail } from "@/pages/Journal";
import Checkout from "@/pages/Checkout";
import OrderSuccess from "@/pages/OrderSuccess";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Account from "@/pages/Account";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminChapters from "@/pages/admin/AdminChapters";
import AdminPreorders from "@/pages/admin/AdminPreorders";
import AdminCMS from "@/pages/admin/AdminCMS";
import AdminInsights from "@/pages/admin/AdminInsights";
import GoogleAuthCallback from "@/components/GoogleAuthCallback";
import { Shipping, Returns, Terms, Cookies } from "@/pages/Policies";
import { api } from "@/lib/api";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function Shell({ children, headerVariant = "light" }) {
  return (
    <>
      <Header variant={headerVariant} />
      {children}
      <Footer />
      <CartDrawer />
    </>
  );
}

function App() {
  useEffect(() => { api.post("/seed").catch(() => {}); }, []);

  return (
    <HelmetProvider>
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Toaster position="top-center" toastOptions={{
            style: { background: "#0a0a0a", color: "#f4f0e8", border: "1px solid #c9a961", borderRadius: 0 }
          }} />
          <GoogleAuthCallback>
          <Routes>
            <Route path="/" element={<Shell headerVariant="dark"><Home /></Shell>} />
            <Route path="/world" element={<Shell headerVariant="dark"><World /></Shell>} />
            <Route path="/chapters" element={<Shell headerVariant="dark"><Chapters /></Shell>} />
            <Route path="/chapters/:roman" element={<Shell headerVariant="dark"><ChapterDetail /></Shell>} />
            <Route path="/collections" element={<Shell><Collections /></Shell>} />
            <Route path="/collections/:slug" element={<Shell headerVariant="dark"><CollectionDetail /></Shell>} />
            <Route path="/product/:slug" element={<Shell><ProductDetail /></Shell>} />
            <Route path="/campaigns" element={<Shell headerVariant="dark"><Campaigns /></Shell>} />
            <Route path="/campaigns/:slug" element={<Shell headerVariant="dark"><CampaignDetail /></Shell>} />
            <Route path="/gifts" element={<Shell><Gifts /></Shell>} />
            <Route path="/shipping" element={<Shell><Shipping /></Shell>} />
            <Route path="/returns"  element={<Shell><Returns /></Shell>} />
            <Route path="/terms"    element={<Shell><Terms /></Shell>} />
            <Route path="/cookies"  element={<Shell><Cookies /></Shell>} />
            <Route path="/founder" element={<Shell><Founder /></Shell>} />
            <Route path="/stores" element={<Shell><Stores /></Shell>} />
            <Route path="/about" element={<Shell><About /></Shell>} />
            <Route path="/careers" element={<Shell><Careers /></Shell>} />
            <Route path="/faq" element={<Shell><FAQ /></Shell>} />
            <Route path="/contact" element={<Shell><Contact /></Shell>} />
            <Route path="/journal" element={<Shell><Journal /></Shell>} />
            <Route path="/journal/:slug" element={<Shell><JournalDetail /></Shell>} />
            <Route path="/checkout" element={<Shell><Checkout /></Shell>} />
            <Route path="/order/success" element={<Shell headerVariant="dark"><OrderSuccess /></Shell>} />
            <Route path="/login" element={<Shell headerVariant="dark"><Login /></Shell>} />
            <Route path="/signup" element={<Shell headerVariant="dark"><Signup /></Shell>} />
            <Route path="/account" element={<Shell><Account /></Shell>} />

            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="chapters" element={<AdminChapters />} />
              <Route path="preorders" element={<AdminPreorders />} />
              <Route path="cms" element={<AdminCMS />} />
              <Route path="insights" element={<AdminInsights />} />
            </Route>
          </Routes>
          </GoogleAuthCallback>
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
